import { useQuery } from "@tanstack/react-query";
import { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { fetchN8nWeather } from "@/lib/weather";
import type { N8nWeatherDay } from "@/lib/weather";
import { 
  type WeatherLocation, 
  DEFAULT_LOCATION 
} from "@/lib/weatherLocations";

export type { N8nWeatherDay };

const STORAGE_KEY = "ipm_weather_location";
const LAST_REFRESH_KEY = "ipm_weather_last_refresh";

function getSavedLocation(): WeatherLocation | null {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved) as WeatherLocation;
    }
  } catch {
    // Ignore parse errors
  }
  return null;
}

function saveLocation(location: WeatherLocation) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(location));
  } catch {
    // Ignore storage errors
  }
}

function getLastRefreshedAt(): number | null {
  try {
    const val = localStorage.getItem(LAST_REFRESH_KEY);
    return val ? Number(val) : null;
  } catch {
    return null;
  }
}

function saveLastRefreshedAt() {
  try {
    localStorage.setItem(LAST_REFRESH_KEY, String(Date.now()));
  } catch {
    // Ignore
  }
}

export const useWeatherData = () => {
  const [selectedLocation, setSelectedLocation] = useState<WeatherLocation>(
    () => getSavedLocation() || DEFAULT_LOCATION
  );

  const [lastRefreshedAt, setLastRefreshedAt] = useState<number | null>(
    () => getLastRefreshedAt()
  );

  const { i18n } = useTranslation();
  const lang = i18n.language;

  const weatherQuery = useQuery({
    queryKey: [
      "n8n-weather",
      selectedLocation.city,
      selectedLocation.countryCode,
      // Dos ciudades pueden llamarse igual en países distintos: las coordenadas son
      // lo que de verdad distingue una consulta de otra.
      selectedLocation.lat ?? null,
      selectedLocation.lon ?? null,
      // La descripción del tiempo viene traducida por la API: cambiar de idioma es
      // una consulta distinta.
      lang,
    ],
    queryFn: () =>
      fetchN8nWeather(
        selectedLocation.city,
        selectedLocation.countryCode,
        selectedLocation.lat,
        selectedLocation.lon,
        lang,
      ),
    // Auto-fetch on first mount (and on location change via the query key) so the
    // Weekly Forecast widget AND the welcome splash bento always have real data
    // without requiring the user to tap "Refresh".
    retry: 2,
    // Diez minutos: ahora el número grande es la temperatura de AHORA, no un
    // pronóstico, y media hora de caché se notaba. La función también cachea diez
    // minutos en el servidor, así que refrescar de más no cuesta llamadas de pago.
    staleTime: 10 * 60 * 1000,
    gcTime: 6 * 60 * 60 * 1000, // 6 h — keep cache warm across the session
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
    // Con el dashboard abierto sin tocar nada (tablet en la nave), el parte se
    // renueva solo. Open-Meteo es el proveedor principal desde 2026-08-16 y no
    // cobra por llamada, así que el intervalo no cuesta dinero.
    refetchInterval: 15 * 60 * 1000,
    refetchIntervalInBackground: false,
  });

  // Persist last-refresh timestamp whenever the query successfully resolves
  // (covers both the auto cold-start fetch and manual refreshes).
  useEffect(() => {
    if (weatherQuery.dataUpdatedAt && weatherQuery.isSuccess) {
      saveLastRefreshedAt();
      setLastRefreshedAt(weatherQuery.dataUpdatedAt);
    }
  }, [weatherQuery.dataUpdatedAt, weatherQuery.isSuccess]);

  const refetch = useCallback(async () => {
    const result = await weatherQuery.refetch();
    if (result.data) {
      saveLastRefreshedAt();
      setLastRefreshedAt(Date.now());
    }
    return result;
  }, [weatherQuery.refetch]);

  const changeLocation = useCallback((location: WeatherLocation) => {
    saveLocation(location);
    setSelectedLocation(location);
  }, []);

  // Note: changing `selectedLocation` updates the React Query key, which
  // automatically triggers a fresh fetch — no manual refetch effect needed.

  return {
    data: weatherQuery.data,
    isLoading: weatherQuery.isLoading,
    isFetching: weatherQuery.isFetching,
    error: weatherQuery.error,
    refetch,
    selectedLocation,
    changeLocation,
    lastRefreshedAt,
  };
};
